import { atom, selector, selectorFamily } from 'recoil'
import { keyFactory } from 'src/utils/recoil'
import { billingClient } from 'src/clients'
import { BillingSubscription, BillingPaymentMethod, BillingInvoice, BillingUpcomingInvoice } from 'src/common-services'
import { currentUser } from 'src/atoms/user'
import { wait } from 'src/utils/wait'

const settingKey = keyFactory('billing')

type UpcomingInvoiceForUI = BillingUpcomingInvoice & {
  paymentMethod?: BillingPaymentMethod
}

export const subscription = selector<BillingSubscription | undefined>({
  key: settingKey('subscription'),
  get: ({ get }) => {
    const user = get(currentUser)
    if (!user || user.plan === 'none') {
      return undefined
    }
    return billingClient.getSubscription()
  },
})

export const paymentMethods = selector<BillingPaymentMethod[] | undefined>({
  key: settingKey('payment-methods'),
  get: ({ get }) => {
    const user = get(currentUser)
    if (!user) {
      return undefined
    }
    return billingClient.getPaymentMethods()
  },
})

export const defaultPaymentMethod = selector<BillingPaymentMethod | undefined>({
  key: settingKey('default-payment-method'),
  get: ({ get }) => {
    const pms = get(paymentMethods)
    if (!pms) {
      return undefined
    }
    return pms.find((method) => method.default)
  },
})

export const invoices = selector<BillingInvoice[]>({
  key: settingKey('invoices'),
  get: () => {
    return billingClient.getInvoices()
  },
})

export const invoiceById = selectorFamily<BillingInvoice | undefined, string | undefined>({
  key: settingKey('invoice-by-id'),
  get: (id) => async () => {
    if (!id) {
      return undefined
    }
    return billingClient.getInvoiceById(id)
  },
})

export const upcomingInvoice = selector<UpcomingInvoiceForUI | undefined>({
  key: settingKey('upcoming-invoice'),
  get: async ({ get }) => {
    const upcomingInvoice: UpcomingInvoiceForUI | undefined = await billingClient.getUpcomingInvoice()
    if (!upcomingInvoice) {
      return undefined
    }
    const defaultPm = get(defaultPaymentMethod)
    upcomingInvoice.paymentMethod = defaultPm
    return upcomingInvoice
  },
})

export const isEditPlan = atom<boolean>({
  key: settingKey('is-edit-plan'),
  default: false,
})

export const isChangePaymentMethodComplete = atom<boolean>({
  key: settingKey('is-change-payment-method-complete'),
  default: true,
})

export const isStripeCustomerExists = selector<boolean>({
  key: settingKey('is-stripe-customer-exists'),
  get: async ({ get }) => {
    const user = get(currentUser)
    if (!user || user.tier === 'premium') {
      return false
    }

    let exists = false
    let tries = 0
    while (!exists && tries < 25) {
      const isExist = await billingClient.getIsStripeCustomerExists()
      if (isExist) {
        exists = true
      }
      await wait(5000)
      tries++
    }
    return exists
  },
})
